import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ManagementContentSkeleton } from '../layout/ManagementPageSkeleton';
import ConfirmDialog from '../common/ConfirmDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ServicePackageCard from './ServicePackageCard';
import ServicePackageEditorDialog from './ServicePackageEditorDialog';
import { getServicePackageErrorKey } from './servicePackageErrors';
import { getServicePackageName } from './servicePackageDisplay';
import { useToast } from '../../context/ToastContext';
import ServicePackageService, { isServicePackageActive } from '../../services/servicePackageService';
import TeamMemberService from '../../services/teamMemberService';
import { filterAgendaVisibleTeamMembers } from '../../utils/teamMemberBookingUtils';
import requestDeduplicator from '../../utils/requestDeduplicator';
import useStoreConsentForms from '../../hooks/useStoreConsentForms';

const CLOSED_DIALOG_STATE = { open: false, mode: 'create', servicePackage: null };
const DEFAULT_LANGUAGES = ['en'];

const ServicePackagesPanel = ({ storeId, canWrite }) => {
  const { t, i18n } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [supportedLanguages, setSupportedLanguages] = useState(DEFAULT_LANGUAGES);
  const [currency, setCurrency] = useState('EUR');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogState, setDialogState] = useState(CLOSED_DIALOG_STATE);
  const [statusChangePackage, setStatusChangePackage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusChangePackageId, setStatusChangePackageId] = useState(null);
  const consentForms = useStoreConsentForms(storeId);

  const loadData = useCallback(async () => {
    if (!storeId) {
      setError('servicePackagesManagement.error.noStoreSelected');
      setLoading(false);
      return;
    }

    return requestDeduplicator.deduplicate(`service-packages-panel-${storeId}`, async () => {
      try {
        setLoading(true);
        setError(null);
        const [data, teamMembersResult] = await Promise.all([
          ServicePackageService.getServicePackageManagementData(storeId),
          TeamMemberService.getTeamMembersByStore(storeId).catch(() => []),
        ]);
        setPackages(data.packages);
        setServices(data.services);
        setSupportedLanguages(data.supportedLanguages);
        setCurrency(data.currency);
        setTeamMembers(filterAgendaVisibleTeamMembers(teamMembersResult));
      } catch (loadError) {
        console.error('Failed to load service packages:', loadError);
        setError(getServicePackageErrorKey(loadError, 'servicePackagesManagement.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  }, [storeId]);

  const refreshPackages = useCallback(async () => {
    const nextPackages = await ServicePackageService.getServicePackages(storeId);
    setPackages(nextPackages);
  }, [storeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSavePackage = async (servicePackage) => {
    const isEdit = dialogState.mode === 'edit' && dialogState.servicePackage?.id;

    try {
      setSaving(true);
      if (isEdit) {
        await ServicePackageService.updateServicePackage(storeId, dialogState.servicePackage.id, servicePackage);
      } else {
        await ServicePackageService.createServicePackage(storeId, servicePackage);
      }

      showSuccess(t(isEdit ? 'servicePackagesManagement.feedback.updated' : 'servicePackagesManagement.feedback.created'));
      setDialogState(CLOSED_DIALOG_STATE);
      await refreshPackages();
    } catch (saveError) {
      console.error('Failed to save service package:', saveError);
      showError(t(getServicePackageErrorKey(
        saveError,
        isEdit ? 'servicePackagesManagement.error.update' : 'servicePackagesManagement.error.create',
      )));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    const target = statusChangePackage;
    if (!target) {
      return;
    }

    const wasActive = isServicePackageActive(target.status);

    try {
      setStatusChangePackageId(target.id);
      if (wasActive) {
        await ServicePackageService.deactivateServicePackage(storeId, target.id);
      } else {
        await ServicePackageService.activateServicePackage(storeId, target.id);
      }

      showSuccess(t(wasActive
        ? 'servicePackagesManagement.feedback.deactivated'
        : 'servicePackagesManagement.feedback.activated'));
      await refreshPackages();
    } catch (statusError) {
      console.error('Failed to change service package status:', statusError);
      showError(t(getServicePackageErrorKey(
        statusError,
        wasActive ? 'servicePackagesManagement.error.deactivate' : 'servicePackagesManagement.error.activate',
      )));
    } finally {
      setStatusChangePackageId(null);
      setStatusChangePackage(null);
    }
  };

  if (loading) {
    return <ManagementContentSkeleton variant="cards" withAction={canWrite} />;
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>{t(error)}</Alert>
        <Button id="service-packages-retry" variant="contained" onClick={loadData}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  const statusChangeIsActive = isServicePackageActive(statusChangePackage?.status);
  const statusChangeKey = statusChangeIsActive ? 'deactivate' : 'activate';

  return (
    <Box id="service-packages-content" sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {canWrite ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            mb: { xs: 2.5, sm: 4 },
            pt: { xs: 0.5, sm: 1 },
          }}
        >
          <PrimaryActionButton
            id="service-packages-add"
            startIcon={<AddIcon />}
            onClick={() => setDialogState({ open: true, mode: 'create', servicePackage: null })}
            sx={{
              px: { xs: 2.5, sm: 4 },
              py: 1.5,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('servicePackagesManagement.addNew')}
          </PrimaryActionButton>
        </Box>
      ) : null}

      {packages.length === 0 ? (
        <Box id="service-packages-empty" sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('servicePackagesManagement.noPackages')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('servicePackagesManagement.emptyHint')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3} p={1} sx={{ pt: 2 }}>
          {packages.map((servicePackage) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={servicePackage.id}>
              <ServicePackageCard
                servicePackage={servicePackage}
                currency={currency}
                services={services}
                teamMembers={teamMembers}
                loading={saving || statusChangePackageId === servicePackage.id}
                canEdit={canWrite}
                onEdit={() => setDialogState({ open: true, mode: 'edit', servicePackage })}
                onToggleStatus={() => setStatusChangePackage(servicePackage)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <ServicePackageEditorDialog
        open={dialogState.open}
        mode={dialogState.mode}
        servicePackage={dialogState.servicePackage}
        supportedLanguages={supportedLanguages}
        currency={currency}
        services={services}
        teamMembers={teamMembers}
        consentForms={consentForms}
        saving={saving}
        onClose={() => setDialogState(CLOSED_DIALOG_STATE)}
        onSave={handleSavePackage}
      />

      <ConfirmDialog
        open={Boolean(statusChangePackage)}
        onClose={() => setStatusChangePackage(null)}
        onConfirm={handleToggleStatus}
        title={t(`servicePackagesManagement.${statusChangeKey}.title`)}
        message={t(`servicePackagesManagement.${statusChangeKey}.message`, {
          name: statusChangePackage ? getServicePackageName(statusChangePackage.name, i18n.language, t) : '',
        })}
        confirmText={t(`servicePackagesManagement.${statusChangeKey}.confirm`)}
        cancelText={t('common.cancel')}
        confirmColor={statusChangeIsActive ? 'error' : 'success'}
      />
    </Box>
  );
};

export default ServicePackagesPanel;
